import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../../hooks/useAuth';
import {
  useTarjetas,
  useCrearTarjeta,
  useActualizarTarjeta,
  useEliminarTarjeta
} from '../../hooks/useReferencias';
import { CrudTable, CrudForm, estadoColumn } from '../../components/admin/crud';
import { estadoField } from '../../components/admin/crud/CrudForm';
import { z } from 'zod';

import type { Tarjeta } from '../../types';
import type { CrearTarjeta } from '../../services/referenciasService';

const companiaField = {
  name: 'compania',
  label: 'Compañía',
  type: 'text' as const,
  placeholder: 'Compañía emisora de la tarjeta',
  validation: z.string().optional(),
};

const numeroField = {
  name: 'numero',
  label: 'Número',
  type: 'text' as const,
  placeholder: 'Número de la tarjeta',
  required: true,
  validation: z.string().min(1, 'Número es requerido').max(100, 'Número muy largo'),
};

const descripcionField = {
  name: 'descripcion',
  label: 'Descripción',
  type: 'text' as const,
  placeholder: 'Descripción (opcional)',
  validation: z.string().optional(),
};

export const TarjetasView: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();

  const canCreate = hasPermission('tarjetas.crear');
  const canEdit = hasPermission('tarjetas.editar');
  const canDelete = hasPermission('tarjetas.eliminar');

  const [filtros, setFiltros] = useState({ page: 1, limit: 10 });
  const [formModal, setFormModal] = useState<{
    isOpen: boolean;
    mode: 'create' | 'edit';
    data?: Tarjeta;
  }>({
    isOpen: false,
    mode: 'create',
  });

  const { data, isLoading, error } = useTarjetas(filtros);
  const crearMutation = useCrearTarjeta();
  const actualizarMutation = useActualizarTarjeta();
  const eliminarMutation = useEliminarTarjeta();

  const columns = useMemo(() => [
    { key: 'compania', title: 'Compañía', width: '200px', render: (v: string) => v || '-' },
    { key: 'numero', title: 'Número', width: '180px' },
    { key: 'descripcion', title: 'Descripción', width: '240px', render: (v: string) => v || '-' },
    estadoColumn<Tarjeta>(),
  ], []);

  const filters = useMemo(() => [
    {
      key: 'search',
      label: t('admin.search_placeholder'),
      type: 'text' as const,
      placeholder: t('admin.search_placeholder'),
    },
  ], [t]);

  const handleFiltersChange = (newFilters: Record<string, any>) => {
    setFiltros({ ...filtros, ...newFilters });
  };

  const handlePageChange = (page: number) => {
    setFiltros({ ...filtros, page });
  };

  const handleAdd = () => {
    if (!canCreate) {
      toast.error(t('errors.no_permission'));
      return;
    }
    setFormModal({ isOpen: true, mode: 'create' });
  };

  const handleEdit = (tarjeta: Tarjeta) => {
    setFormModal({ isOpen: true, mode: 'edit', data: tarjeta });
  };

  const handleDelete = async (tarjeta: Tarjeta) => {
    try {
      await eliminarMutation.mutateAsync(tarjeta.id);
      toast.success(t('admin.delete_success'));
    } catch (error) {
      toast.error(t('errors.delete_error'));
      console.error('Error eliminando tarjeta:', error);
    }
  };

  const handleFormSubmit = async (datos: CrearTarjeta) => {
    try {
      if (formModal.mode === 'create') {
        await crearMutation.mutateAsync(datos);
        toast.success(t('admin.create_success'));
      } else if (formModal.data) {
        await actualizarMutation.mutateAsync({ id: formModal.data.id, datos });
        toast.success(t('admin.update_success'));
      }
      setFormModal({ isOpen: false, mode: 'create' });
    } catch (error: any) {
      const message = error?.response?.data?.detail || t('errors.save_error');
      toast.error(message);
      console.error('Error en formulario tarjeta:', error);
    }
  };

  const handleFormClose = () => {
    setFormModal({ isOpen: false, mode: 'create' });
  };

  const isFormLoading = crearMutation.isLoading || actualizarMutation.isLoading;

  return (
    <div className="space-y-6">
      <div className="flex items-center space-x-2 text-sm text-gray-500">
        <button onClick={() => navigate('/admin')} className="hover:text-gray-700 transition-colors">
          {t('admin.administration')}
        </button>
        <span>/</span>
        <span className="text-gray-900 font-medium">{t('admin.tarjetas')}</span>
      </div>

      <div className="flex items-center">
        <button
          onClick={() => navigate('/admin')}
          className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 flex items-center space-x-2"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
          </svg>
          <span>{t('common.back_to_admin')}</span>
        </button>
      </div>

      <CrudTable
        data={data}
        isLoading={isLoading}
        error={error as Error | null}
        columns={columns}
        filters={filters}
        currentFilters={filtros}
        onFiltersChange={handleFiltersChange}
        onPageChange={handlePageChange}
        onAdd={handleAdd}
        onEdit={canEdit ? handleEdit : undefined}
        onDelete={canDelete ? handleDelete : undefined}
        title={t('admin.tarjetas')}
        addButtonText={t('admin.add')}
        canAdd={canCreate}
        canEdit={canEdit}
        canDelete={canDelete}
      />

      <CrudForm
        isOpen={formModal.isOpen}
        onClose={handleFormClose}
        onSubmit={handleFormSubmit}
        fields={[companiaField, numeroField, descripcionField, estadoField]}
        title={formModal.mode === 'create' ? t('admin.tarjeta_create') : t('admin.tarjeta_edit')}
        submitText={t('common.save')}
        isLoading={isFormLoading}
        initialData={formModal.data}
        mode={formModal.mode}
      />
    </div>
  );
};

export default TarjetasView;
